import { useFieldArray, useForm, useWatch } from "react-hook-form";
import {
  DatePicker,
  Input,
  Label,
  MultipleSelect,
  Select,
  Slider,
  Submit,
  TextArea,
} from "../FormComponents";
import useLabels from "../../hooks/useLabels";
import {
  add,
  empty,
  setOperations,
  updateOp,
} from "../../context/operations/operationsSlice";
import { useNavigate, useParams } from "react-router";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useDispatch, useSelector } from "react-redux";
import {
  applyWorkingProtocolOperation,
  getDrugs,
  getOperations,
  getProtocols,
  getWarehouseByType,
  newOperation,
  updateOperation,
  updateWorkingProtocol,
} from "../../services/utils";
import {
  DestinationSelector,
  LotOrTankOperationSelector,
  SourceSelector,
  TankIcon,
  WineMakingProtocolModalChildren,
  WineMakingProtocolRowModal,
} from "../Generic";
import { INT_REGEX, NEGATIVE_POSITIVE_INT_FLOAT_REGEX } from "../../constants/regex";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  selectDrugs,
  selectOperations,
  selectWarehouseByType,
  selectWineMaking,
} from "../../context/selectors";
import { addMinutes } from "date-fns";
import { setWineMaking } from "../../context/protocols/wineMakingSlice";
import TableEdit from "../TableEdit";
import { createColumnHelper } from "@tanstack/react-table";
import TableCell from "../TableEdit/TableCell";
import EditCell from "../TableEdit/EditCell";
import TransferForm from "./TransferForm";
import { setDrugs } from "../../context/substances/drugsSlice";
import { setWarehouse } from "../../context/warehouse/warehouseSlice";

const WineMakingForm = ({
  operation,
  lots,
  activeWinery,
  tanks,
  users,
  configs,
  type,
  isDuplicate = false,
  isMobile = false,
}) => {
  const { id } = useParams();
  const [getLabel] = useLabels();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const dispatch = useDispatch();
  const drugs = useSelector(selectDrugs);
  const operations = useSelector(selectOperations);
  const protocols = useSelector(selectWineMaking);
  const machineries = useSelector(selectWarehouseByType("machineries"));

  const [checkbox, setCheckbox] = useState(false);
  const [protocol, setProtocol] = useState();
  const [operationsP, setOperationsP] = useState([]);

  const pType =
    type === "CLEANING" ? "CIP" : type === "PHYSICAL" ? type : type === "WINEMAKINGPOST" ? "POST" : "PRE";
  const [srcTankEnabledFieldArray, setSrcTankEnabledFieldArray] = useState([]);
  const [isExactDate, setIsExactDate] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModifyMode, setIsModifyMode] = useState(-1);

  const setCheckboxState = () => {
    setCheckbox(!checkbox);
  };

  const setModifyMode = (row_id) => {
    setIsModifyMode(row_id);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    if (errors?.day) clearErrors("day");
  };

  const updateTableData = () => {
    // console.log("errors", errors, "modalErrors", modalErrors, 'modalGetValues', modalGetValues());
    // return
    const {
      id,
      day,
      work_time,
      pause_time,
      motor_speed,
      iter_per_day,
      homogenization_type,
      oxygenation_dose_hours,
      oxygenation_dose_minutes,
      protocol_type,
      functional,
      quantity,
      operation,
      phase,
      product,
      goal,
      note,
    } = modalGetValues();
    // console.log('op', operation, 'modalGetValues()', modalGetValues())
    const opValue = operation?.value;
    const index = operationsP?.findIndex((o) => o?.id === Number(id));
    
    const newItem = {
      phase: phase?.value,
      operation: operation?.value,
      id: Number(id),
      opType: "WINEMAKINGPROTOCOL",
      functional: functional?.value,
      day: Number(day),
      goal,
      note,
    };

    if (opValue === "ADD" && (functional?.value || type === "CLEANING")) {
      const drug = drugs?.find((d) => d?.id === Number(product?.value));

      if (!drug) {
        setIsModalOpen(false);
        return;
      }

      newItem.product = {
        ...drug,
        value: drug?.id,
        label: drug?.name + (drug?.unit ? " (" + drug?.unit + ")" : ""),
      };
      // product: drug?.id,
      // product_name: drug?.name,
      // product_type: drug?.type,
      // product_unit: drug?.unit,
      // product_unit_id: drug?.unit_id,
      newItem.quantity = Number(quantity);
      newItem.work_time = work_time;
    } else if (opValue === "OXYGENATION") {
      newItem.protocol_type = protocol_type?.value;
      newItem.quantity = Number(quantity);
      newItem.oxygenation_dose_hours = Number(oxygenation_dose_hours) || 0;
      newItem.oxygenation_dose_minutes = Number(oxygenation_dose_minutes) || 0;
    } else if (opValue === "HOMOGENIZATION_MASS") {
      newItem.quantity = Number(quantity);
      newItem.protocol_type = protocol_type?.value;
      if (protocol_type?.value === "HOMOGENIZATION_REASSEMBLY") {
        newItem.homogenization_type = homogenization_type?.value;
        newItem.work_time = work_time;
        newItem.motor_speed = motor_speed;
        newItem.pause_time = pause_time;
        newItem.iter_per_day = iter_per_day;
      }
    }

    // Crea una copia modificata localmente di operationsP
    const updated = [...(operationsP || [])];

    if (index > -1) {
      // Modify existing
      updated.splice(index, 1, newItem);
    } else {
      // Add new
      updated.push(newItem);
    }

    // Ordina per day
    updated.sort((a, b) => (a.day ?? 0) - (b.day ?? 0));

    // Validazione
    if (!validateSteps(updated, false)) {
      setError("day", {
        type: "manual",
        message: getLabel("errorStepNotIncremental"),
      });
      // Il toast viene gestito internamente da validateSteps
      return;
    }

    // Se valido, aggiorna stato e chiudi modal
    setOperationsP(updated);

    console.log("updated", updated);
    setIsModalOpen(false);
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setError,
    clearErrors,
    reset,
    setValue,
    getValues,
    control,
  } = useForm({
    defaultValues: {
      // dest_tanks: operation?.dest_tanks || [
      //   { tankData: { value: null, label: null } },
      // ],
      src_lots: operation?.src_lots || [
        { lotData: { value: null, label: null } },
      ],
      src_tanks: operation?.src_tanks || [
        { tankData: { value: null, label: null } },
      ],
      priority: operation?.priority || "STANDARD",
      expire_date: operation?.expire_date || null,
      type: { type },
      tank_id_in: operation?.tank_id_in || "",
      cellarman_ids: operation?.cellarman_ids || "",
      new_batch_name: operation?.new_batch_name || "",
      analysisType: { value: "tank" },
    },
    mode: "onTouched",
  });

  const {
    register: modalRegister,
    handleSubmit: modalHandleSubmit,
    formState: { errors: modalErrors },
    setValue: modalSetValue,
    getValues: modalGetValues,
    setError: modalSetError,
    clearErrors: modalClearErrors,
    control: modalControl,
  } = useForm({
    defaultValues: {
      // day: protocol?.ay || "",
      // phase: protocol?.phase || "",
      // operation: protocol?.operation || "",
    },
    mode: "onTouched",
  });

  const srcBatchFieldArray = useFieldArray({
    control,
    name: "src_lots",
  });

  const srcTankFieldArray = useFieldArray({
    control,
    name: "src_tanks",
  });

  const src_lots = useWatch({ control, name: "src_lots" });
  const analysisType = useWatch({ control, name: "analysisType" });
  const watchedOperation = useWatch({ control, name: "operation" });

  const modalOperation = useWatch({
    control: modalControl,
    name: "operation",
  });

  const columnHelper = createColumnHelper();
  const columns2 = [
    columnHelper.accessor(
      (row) => row?.day + (row?.to_day ? ` - ${row?.to_day}` : ""),
      {
        header: getLabel(type === "CIP" ? "protocolStep" : "protocolDay"),
        cell: TableCell,
        meta: {
          type: "number",
          register: modalRegister,
          isFilterDisabled: true,
        },
      }
    ),
    columnHelper.accessor(
      (row) => (row?.phase ? getLabel(`protocolPhase_${row.phase}`) : ""),
      {
        header: getLabel("protocolPhase"),
        cell: TableCell,
        meta: {
          isFilterDisabled: true,
        },
      }
    ),
    columnHelper.accessor(
      (row) =>
        row?.operation ? getLabel(`protocolOperation_${row.operation}`) : "",
      {
        header: getLabel("protocolOperation"),
        cell: TableCell,
        meta: {
          type: "text",
          register: modalRegister,
          isFilterDisabled: true,
        },
      }
    ),
    columnHelper.accessor(
      (row) =>
        pType === "CIP" &&
        (row.operation === "RINSE" || row.operation === "VAPORIZATION")
          ? getLabel("protocolProduct_H2O")
          : row?.product?.name || row?.product_name || "",
      {
        header: getLabel("protocolProduct"),
        cell: TableCell,
        meta: {
          type: "text",
          register: modalRegister,
          isFilterDisabled: true,
        },
      }
    ),
    columnHelper.accessor(
      (row) =>
        row?.quantity
          ? pType === "CIP" &&
            (row.operation === "RINSE" || row.operation === "VAPORIZATION")
            ? `${row?.quantity}`
            : pType === "CIP" && row.operation === "CLEANING"
            ? `${row?.quantity} %`
            : row?.protocol_type === "HOMOGENIZATION_TEMP"
            ? `${row?.quantity} \u00b0C`
            : (row?.quantity || 0) +
              " " +
              (row?.product?.unit || row?.product_unit || "") +
              (row?.protocol_type === "MICRO"
                ? getLabel("protocolOxygenationMonth")
                : row?.protocol_type === "MACRO"
                ? getLabel("protocolOxygenationDay")
                : row?.protocol_type === "CUSTOM"
                ? `/${row?.oxygenation_dose_hours}h:${row?.oxygenation_dose_minutes}m`
                : "")
          : "",
      {
        header: getLabel(
          pType === "CIP" ? "protocolConcentration" : "protocolQuantity"
        ),
        cell: TableCell,
        meta: {
          type: "number",
          register: modalRegister,
          isFilterDisabled: true,
        },
      }
    ),
    // columnHelper.accessor("unit_id", {
    //   header: getLabel("unit"),
    //   cell: TableCell,
    //   meta: {
    //     type: "text",
    //     register: register,
    //   },
    // }),
    // columnHelper.accessor("cost", {
    //   header: getLabel("protocolCost"),
    //   cell: TableCell,
    //   meta: {
    //     type: "number",
    //   },
    // }),
    // ...(pType === "CIP"
    //   ? [
    //       columnHelper.accessor(
    //         (row) => (row?.work_time ? row?.work_time + " min" : ""),
    //         {
    //           header: getLabel("protocolHomogenizationContactTime"),
    //           cell: TableCell,
    //           meta: {
    //             type: "number",
    //             register: register,
    //             isFilterDisabled: true,
    //           },
    //         }
    //       ),
    //     ] : []),
    // : [
    // columnHelper.accessor(
    //   (row) =>
    //     row?.protocol_type === "HOMOGENIZATION_REASSEMBLY"
    //       ? `${getLabel(
    //           "protocolOperation_HOMOGENIZATION_REASSEMBLY"
    //         )} (${getLabel(
    //           `protocolHomogenizationMass_${row?.homogenization_type}`
    //         )})`
    //       : row?.protocol_type === "HOMOGENIZATION_TEMP"
    //       ? getLabel("protocolOperation_HOMOGENIZATION_TEMP")
    //       : row?.protocolType
    //       ? getLabel(`protocolType_${row?.protocolType}`)
    //       : "",
    //   {
    //     header: getLabel("protocolType"),
    //     cell: TableCell,
    //     meta: {
    //       type: "text",
    //       register: register,
    //       // placeholder: getLabel("lotColorPlaceholder"),
    //     },
    //   }
    // ),
    // columnHelper.accessor(
    //   (row) =>
    //     row?.work_time && (row?.pause_time || row?.homogenization_pause_time)
    //       ? `${row?.work_time}min / ${
    //           row?.pause_time || row?.homogenization_pause_time
    //         }h  ${type === 'CLEANING' && row.operation === 'HOMOGENIZATION_MASS' && row?.iter_per_day ? `x ${row?.iter_per_day}` : ''}`
    //       : "",
    //   {
    //     header: getLabel("protocolWorkPauseTime"),
    //     cell: TableCell,
    //     meta: {
    //       type: "number",
    //     },
    //   }
    // ),
    // columnHelper.accessor(
    //   (row) =>
    //     row?.homogenization_motor_speed
    //       ? row?.homogenization_motor_speed + "%"
    //       : "",
    //   {
    //     header: getLabel("protocolMotorSpeed"),
    //     cell: TableCell,
    //     meta: {
    //       type: "number",
    //     },
    //   }
    // ),
    // ]),
    ...(type === "CIP"
      ? [
          columnHelper.accessor(
            (row) => (row?.work_time ? row?.work_time + " min" : ""),
            {
              header: getLabel(
                type === "CLEANING"
                  ? "protocolHomogenizationContactTime"
                  : "protocolWorkTime"
              ),
              cell: TableCell,
              meta: {
                type: "number",
                register: modalRegister,
                isFilterDisabled: true,
              },
            }
          ),
        ]
      : []),
    // columnHelper.accessor((row) => row?.homogenizationpause_time ? (row?.homogenizationpause_time + ' h') : '', {
    //   header: getLabel("protocolPauseTime"),
    //   cell: TableCell,
    //   meta: {
    //     type: "number",
    //     register: register,
    //   },
    // }),
    // columnHelper.accessor((row) => row?.homogenization_motor_speed ? (row?.homogenization_motor_speed + ' %') : '', {
    //   header: getLabel("protocolMotorSpeed"),
    //   cell: TableCell,
    //   meta: {
    //     type: "number",
    //     register: register,
    //   },
    // }),
    // columnHelper.accessor("iterPerDay", {
    //   header: getLabel("protocolIterationPerDay"),
    //   cell: TableCell,
    //   meta: {
    //     type: "number",
    //     register: register,
    //   },
    // }),
    // columnHelper.accessor("oxygenationType", {
    //   header: getLabel("protocolOxygenationType"),
    //   cell: TableCell,
    //   meta: {
    //     type: "text",
    //     register: register,
    //   },
    // }),
    // columnHelper.accessor("goal", {
    //   header: getLabel("protocolGoals"),
    //   cell: TableCell,
    // }),

    // columnHelper.accessor("note", {
    //   header: getLabel("protocolNotes"),
    //   cell: TableCell,
    // }),
    columnHelper.display({
      id: "edit",
      cell: EditCell,
      meta: {
        isEditable: true,
        // isOnlyManual: true,
      },
    }),
  ];

  const computeAddOpTypeString = (row) => {
    if (row?.treatments?.length > 1) {
      return getLabel(`operationType${row.type}`);
    }
    const protocol_type = row?.functional || null;
    const subtype = protocol_type
      ? getLabel(`drugLabelGroup_${protocol_type}`)
      : null;
    return `${getLabel(`protocolType_${row.operation}`)} ${
      subtype ? "(" + subtype?.toLowerCase?.() + ")" : ""
    }`;
  };

  const columns = [
    columnHelper.accessor(
      (row) => row?.day + (row?.to_day ? ` - ${row?.to_day}` : ""),
      {
        header: getLabel(type === "CIP" ? "protocolStep" : "protocolDay"),
        cell: TableCell,
        meta: {
          type: "number",
          register: modalRegister,
          isFilterDisabled: true,
        },
      }
    ),
    ...(type !== "CIP"
      ? [
          columnHelper.accessor(
            (row) => (row?.phase ? getLabel(`protocolPhase_${row.phase}`) : ""),
            {
              header: getLabel("protocolPhase"),
              cell: TableCell,
              // meta: {
              //   type: "text",
              // },
              meta: {
                type: "select",
                isFilterDisabled: true,
                options: [
                  // { value: "Computer Science", label: "Computer Science" },
                  // { value: "Communications", label: "Communications" },
                  // { value: "Business", label: "Business" },
                  // { value: "Psychology", label: "Psychology" },
                ],
              },
            }
          ),
        ]
      : []),
    columnHelper.accessor(
      (row) =>
        row?.operation
          ? row.operation === "ADD"
            ? computeAddOpTypeString(row)
            : getLabel(`protocolOperation_${row.operation}`)
          : "",
      {
        header: getLabel("protocolOperation"),
        cell: TableCell,
        meta: {
          type: "text",
          register: modalRegister,
          isFilterDisabled: true,
        },
      }
    ),
    columnHelper.accessor(
      (row) =>
        row?.operation === "SO2"
          ? ""
          : row.operation === "RINSE" || row.operation === "VAPORIZATION"
          ? getLabel("protocolProduct_H2O")
          : row?.product?.name || row?.product_name || "",
      {
        header: getLabel("protocolProduct"),
        cell: TableCell,
        meta: {
          type: "text",
          register: modalRegister,
          isFilterDisabled: true,
        },
      }
    ),
    ...(type !== "CLEANING"
      ? [
          columnHelper.accessor(
            (row) =>
              row?.protocol_type
                ? row?.operation === "ADD"
                  ? ""
                  : getLabel(`protocolType_${row?.protocol_type}`) +
                    (row?.protocol_type === "HOMOGENIZATION_REASSEMBLY" &&
                    row?.homogenization_type
                      ? ` (${getLabel(
                          `protocolHomogenizationMass_${row?.homogenization_type}`
                        )})`
                      : "") +
                    (row?.protocol_type === "CUSTOM"
                      ? ` (${row?.oxygenation_dose_hours}h:${row?.oxygenation_dose_minutes}m)`
                      : "")
                : "",
            {
              header: getLabel("protocolType"),
              cell: TableCell,
              meta: {
                type: "text",
                isFilterDisabled: true,
              },
            }
          ),
        ]
      : []),
    columnHelper.accessor(
      (row) =>
        row?.quantity
          ? row.operation === "RINSE" || row.operation === "VAPORIZATION"
            ? ""
            : row?.operation === "CLEANING"
            ? `${row?.quantity} %`
            : row?.protocol_type === "HOMOGENIZATION_TEMP"
            ? `${row?.quantity} \u00b0C`
            : (row?.quantity || 0) +
              " " +
              (row?.operation === "OXYGENATION"
                ? getLabel("protocolOxygenationNoDose")
                : row?.product?.unit || row?.product_unit || "")
          : "",
      {
        header: getLabel(
          type !== "CLEANING" ? "protocolQuantity" : "protocolConcentration"
        ),
        cell: TableCell,
        meta: {
          type: "number",
          isFilterDisabled: true,
        },
      }
    ),
    // columnHelper.accessor(
    //   (row) =>
    //     row?.quantity
    //       ? row.operation === "CLEANING"
    //         ? `${row?.quantity} %`
    //         : row?.operation === "SO2"
    //         ? `${row?.quantity} ${row?.product_unit}`
    //         : row?.protocol_type === "HOMOGENIZATION_TEMP"
    //         ? `${row?.quantity} \u00b0C`
    //         : `${row.quantity || 0} ${row?.operation === "OXYGENATION" ? "" :
    //             (row?.product?.unit || row?.product_unit || "")
    //           } ${
    //             row?.operation === "OXYGENATION"
    //               ? row?.protocol_type === "MICRO"
    //                 ? getLabel("protocolOxygenationNoDose_MICRO")
    //                 : row?.protocol_type === "MACRO"
    //                 ? getLabel("protocolOxygenationNoDose_MACRO")
    //                 : row?.protocol_type === "CUSTOM"
    //                 ? `${getLabel("protocolOxygenationNoDose_CUSTOM")}${
    //                     row?.oxygenation_dose_hours || ""
    //                   }h:${
    //                     row?.oxygenation_dose_minutes ||
    //                     ""
    //                   }m`
    //                 : ""
    //               : ""
    //           }`
    //       : "",
    // (row) =>
    //   row?.quantity
    //     ? row?.operation === "RINSE"
    //     ? ""
    //     : row?.operation === "CLEANING"
    //     ? `${row?.quantity} %` :
    //     row?.protocol_type === "HOMOGENIZATION_TEMP"
    //       ? `${row?.quantity} \u00b0C`
    //       : (row?.quantity || 0) +
    //         " " +
    //         (row?.product?.unit || row?.product_unit || "")
    //     : "",
    //   {
    //     header: getLabel(
    //       type === "CIP" ? "protocolConcentration" : "protocolQuantity"
    //     ),
    //     cell: TableCell,
    //     meta: {
    //       type: "number",
    //       register: modalRegister,
    //     },
    //   }
    // ),
    // columnHelper.accessor("oxygenationType", {
    //   header: getLabel("protocolOxygenationType"),
    //   cell: TableCell,
    //   meta: {
    //     type: "text",
    //     register: register,
    //   },
    // }),
  ];

  if (
    operationsP?.find((o) => o?.protocol_type === "HOMOGENIZATION_REASSEMBLY")
  ) {
    // columns.splice(
    //   6,
    //   0,
    columns.push(
      ...[
        // columnHelper.accessor(
        //   (row) =>
        //     row?.protocol_type && row?.protocol_type !== "ADD"
        //       ? row?.operation === "OXYGENATION"
        //         ? getLabel(`protocolOXYGENATION_${row?.protocol_type}`)
        //         : getLabel(`protocolType_${row?.protocol_type}`) +
        //           (row?.protocol_type === "HOMOGENIZATION_REASSEMBLY" &&
        //           row?.homogenization_type
        //             ? ` (${getLabel(
        //                 `protocolHomogenizationMass_${row?.homogenization_type}`
        //               )})`
        //             : "") +
        //           (row?.protocol_type === "CUSTOM"
        //             ? ` (${row?.oxygenation_dose_hours}h:${row?.oxygenation_dose_minutes}m)`
        //             : "")
        //       : "",
        //   {
        //     header: getLabel("protocolType"),
        //     cell: TableCell,
        //     meta: {
        //       type: "text",
        //       register: modalRegister,
        //       // placeholder: getLabel("lotColorPlaceholder"),
        //     },
        //   }
        // ),
        columnHelper.accessor(
          (row) =>
            row?.work_time &&
            (row?.pause_time || row?.homogenization_pause_time)
              ? `${row?.work_time} min/${
                  row?.pause_time || row?.homogenization_pause_time
                }h ${
                  (type === "CIP" || row.operation === "HOMOGENIZATION_MASS") &&
                  row?.iter_per_day
                    ? `x ${row?.iter_per_day}`
                    : ""
                }`
              : "",
          {
            header: getLabel("protocolWorkPauseTime"),
            cell: TableCell,
            meta: {
              type: "number",
              register: modalRegister,
              isFilterDisabled: true,
            },
          }
        ),
        columnHelper.accessor(
          (row) => (row?.motor_speed ? row?.motor_speed + " %" : ""),
          {
            header: getLabel("protocolMotorSpeed"),
            cell: TableCell,
            meta: {
              type: "number",
              register: modalRegister,
              isFilterDisabled: true,
            },
          }
        ),
      ]
    );
  }

  if (type === "CLEANING") {
    columns.push(
      columnHelper.accessor(
        (row) => (row?.work_time ? `${row?.work_time} min` : ""),
        {
          header: getLabel("protocolHomogenizationContactTime"),
          cell: TableCell,
          meta: {
            type: "text",
            isFilterDisabled: true,
          },
        }
      )
    );
  }

  columns.push(
    columnHelper.display({
      id: "edit",
      cell: EditCell,
      meta: {
        isEditable: true,
      },
    })
  );

  // const columns = [
  //   ...(type !== "PHYSICAL"
  //     ? [
  //         columnHelper.accessor(
  //           (row) => row?.day + (row?.to_day ? ` - ${row?.to_day}` : ""),
  //           {
  //             header: getLabel("protocolDay"),
  //             cell: TableCell,
  //             meta: {
  //               type: "number",
  //             },
  //           }
  //         ),
  //       ]
  //     : []),
  //   columnHelper.accessor(
  //     (row) => (row?.phase ? getLabel(`protocolPhase_${row.phase}`) : ""),
  //     {
  //       header: getLabel("protocolPhase"),
  //       cell: TableCell,
  //     }
  //   ),
  //   columnHelper.accessor(
  //     (row) =>
  //       row?.operation
  //         ? row?.operation === "ADD"
  //           ? computeAddOpTypeString(row)
  //           : getLabel(`protocolOperation_${row.operation}`)
  //         : "",
  //     {
  //       header: getLabel(
  //         type === "PHYSICAL"
  //           ? "protocolStabilizationMode"
  //           : "protocolOperation"
  //       ),
  //       cell: TableCell,
  //       meta: {
  //         type: "text",
  //       },
  //     }
  //   ),
  //   ...(type === "PHYSICAL"
  //     ? [
  //         columnHelper.accessor(
  //           (row) =>
  //             row?.temperature +
  //             (row?.temperature ? `${row?.temperature} °C` : ""),
  //           {
  //             header: getLabel("protocolTemperature"),
  //             cell: TableCell,
  //             meta: {
  //               type: "number",
  //             },
  //           }
  //         ),
  //       ]
  //     : []),
  //   columnHelper.accessor(
  //     (row) =>
  //       row?.operation === "SO2"
  //         ? ""
  //         : row?.operation === "RINSE"
  //         ? getLabel("protocolProduct_H2O")
  //         : row?.product?.name || row?.product_name || "",
  //     {
  //       header: getLabel("protocolProduct"),
  //       cell: TableCell,
  //       meta: {
  //         type: "text",
  //       },
  //     }
  //   ),
  //   columnHelper.accessor(
  //     (row) =>
  //       row?.quantity
  //         ? row?.operation === "RINSE"
  //           ? ""
  //           : row?.operation === "CLEANING"
  //           ? `${row?.quantity} %`
  //           : row?.protocol_type === "HOMOGENIZATION_TEMP"
  //           ? `${row?.quantity} \u00b0C`
  //           : (row?.quantity || 0) +
  //             " " +
  //             (row?.operation === "OXYGENATION"
  //               ? getLabel("protocolOxygenationNoDose")
  //               : row?.product?.unit || row?.product_unit || "")
  //         : "",
  //     {
  //       header: getLabel(
  //         type !== "CIP" ? "protocolQuantity" : "protocolConcentration"
  //       ),
  //       cell: TableCell,
  //       meta: {
  //         type: "number",
  //       },
  //     }
  //   ),
  //   // columnHelper.accessor("cost", {
  //   //   header: getLabel("protocolCost"),
  //   //   cell: TableCell,
  //   //   meta: {
  //   //     type: "number",
  //   //   },
  //   // }),
  //   ...(type !== "CIP"
  //     ? [
  //         // columnHelper.accessor("iterPerDay", {
  //         //   header: getLabel("protocolIterationPerDay"),
  //         //   cell: TableCell,
  //         //   meta: {
  //         //     type: "number",
  //         //   },
  //         // })
  //       ]
  //     : [
  //         columnHelper.accessor(
  //           (row) =>
  //             row?.operation === "HOMOGENIZATION_MASS"
  //               ? ""
  //               : row?.work_time
  //               ? `${row?.work_time} min`
  //               : "",
  //           {
  //             header: getLabel("protocolHomogenizationContactTime"),
  //             cell: TableCell,
  //             meta: {
  //               type: "number",
  //             },
  //           }
  //         ),
  //       ]),
  //   // columnHelper.accessor("goal", {
  //   //   header: getLabel("protocolGoals"),
  //   //   cell: TableCell,
  //   // }),
  //   // columnHelper.accessor("note", {
  //   //   header: getLabel("protocolNotes"),
  //   //   cell: TableCell,
  //   // }),
  //   // columnHelper.accessor("oxygenationType", {
  //   //   header: getLabel("protocolOxygenationType"),
  //   //   cell: TableCell,
  //   //   meta: {
  //   //     type: "text",
  //   //     register: register,
  //   //   },
  //   // }),
  // ];

  // if (
  //   operationsP?.find((o) => o?.protocol_type === "HOMOGENIZATION_REASSEMBLY")
  // ) {
  //   columns.splice(
  //     5,
  //     0,
  //     columnHelper.accessor(
  //       (row) =>
  //         row?.protocol_type
  //           ? row?.operation === "ADD"
  //             ? ""
  //             : getLabel(`protocolType_${row?.protocol_type}`) +
  //               (row?.protocol_type === "HOMOGENIZATION_REASSEMBLY" &&
  //               row?.homogenization_type
  //                 ? ` (${getLabel(
  //                     `protocolHomogenizationMass_${row?.homogenization_type}`
  //                   )})`
  //                 : "") +
  //               (row?.protocol_type === "CUSTOM"
  //                 ? ` (${row?.oxygenation_dose_hours}h:${row?.oxygenation_dose_minutes}m)`
  //                 : "")
  //           : "",
  //       {
  //         header: getLabel("protocolType"),
  //         cell: TableCell,
  //         meta: {
  //           type: "text",
  //         },
  //       }
  //     ),
  //     columnHelper.accessor(
  //       (row) =>
  //         row?.work_time && (row?.pause_time || row?.homogenization_pause_time)
  //           ? `${row?.work_time} min/${
  //               row?.pause_time || row?.homogenization_pause_time
  //             }h ${
  //               (type === "CIP" || row.operation === "HOMOGENIZATION_MASS") &&
  //               row?.iter_per_day
  //                 ? `x ${row?.iter_per_day}`
  //                 : ""
  //             }`
  //           : "",
  //       {
  //         header: getLabel("protocolWorkPauseTime"),
  //         cell: TableCell,
  //         meta: {
  //           type: "number",
  //         },
  //       }
  //     ),
  //     columnHelper.accessor(
  //       (row) =>
  //         row?.motor_speed
  //           ? row?.motor_speed + "%"
  //           : "",
  //       {
  //         header: getLabel("protocolMotorSpeed"),
  //         cell: TableCell,
  //         meta: {
  //           type: "number",
  //         },
  //       }
  //     )
  //   );
  // }

  // columns.push(columnHelper.display({
  //     id: "edit",
  //     cell: EditCell,
  //     meta: {
  //       isEditable: true,
  //       // isOnlyManual: true,
  //     },
  //   }));

  // const { dest_tanks, protocol_id } = useWatch({ control });
  const src_tanks = useWatch({ control, name: "src_tanks" });
  const protocol_id = useWatch({ control, name: "protocol_id" });
  const protocol_subtype = useWatch({ control, name: "protocol_subtype" });

  // const { fields, append, prepend, remove, swap, move, insert, replace } =
  //   useFieldArray({
  //     control,
  //     name: "dest_tanks",
  //   });

  // const { fields, append, prepend, remove, swap, move, insert, replace } =
  //   useFieldArray({
  //     control,
  //     name: "src_tanks",
  //   });

  const loadOperations = async () => {
    const loadedOperations = await getOperations(axiosPrivate);
    dispatch(setOperations(loadedOperations));
  };

  const loadProtocols = async () => {
    const loadedProtocols = await getProtocols("winemaking", axiosPrivate);
    dispatch(setWineMaking(loadedProtocols));
  };

  const loadDrugs = async () => {
    const currentDrugs = await getDrugs(axiosPrivate);
    dispatch(setDrugs(currentDrugs));
  };

  const loadWarehouse = async () => {
    const currentData = await getWarehouseByType(axiosPrivate, "machineries");
    dispatch(setWarehouse({ type: "machineries", data: currentData }));
  };

  useEffect(() => {
    if (!machineries || machineries.length === 0) loadWarehouse();
    if (!drugs || drugs.length === 0) loadDrugs();
    if (!protocols || protocols?.length === 0) loadProtocols();
    if (id && (!operations || operations?.length === 0)) loadOperations();
    if (id && operations?.length > 0) {
      const operation = operations?.find((o) => o.id === Number(id)) || null;

      if (operation === null) return;
      
      if (["CLEANING", "STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(operation?.type)) {
        const protocol = protocols?.find(
          (p) => p?.id === Number(operation?.winemaking_protocol_id)
        );

        setValue("protocol_subtype",
          ["STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(protocol?.type) ?
          { value: "physicalProtocol_STABILIZATION",
            label: getLabel("physicalProtocol_STABILIZATION"),
          } :
          { value: "physicalProtocol_FILTRATION",
            label: getLabel("physicalProtocol_FILTRATION")
          }
        );

        setValue("protocol_id", {
          // ...protocol,
          value: protocol?.id,
          label: protocol?.name,
          protocol_subtype: protocol?.protocol_subtype,
          protocol_type: protocol?.protocol_type,
          operations: protocol?.operations,
          // name: name,
        });

        if (operation?.type === "STABILIZATION_RESIN") {

          const physical_percentage = (operation?.treatments || [])?.map(t => Number(t?.quantity || 0));
          setValue("physical_percentage", physical_percentage)
        }
      }

      setValue("priority", {
        label: getLabel(`operationPriority${operation?.priority}`),
        value: operation?.priority,
      });

      const dests = [];
      // const opTanks = type === 'CLEANING' ? operation?.src_tanks : operation?.dest_tanks;
      operation?.src_tanks?.map((s) => {
        const tank = tanks?.find((t) => t.id === s.tank_id);
        dests.push({
          tankData: {
            value: tank?.id,
            label: tank?.name,
            name: tank?.name,
            capacity: Number(tank?.capacity),
            quantity: Number(tank?.quantity),
            availability: Number(tank?.capacity) - Number(tank?.quantity),
            color: tank?.color,
            lot:
              lots?.find((lot) => lot?.id === tank?.batch_id)?.name ||
              getLabel("tankState_EMPTY"),
          },
        });
      });

      srcTankFieldArray.replace(dests);

      const cellarmans = [];
      operation?.cellarman_ids?.map((u) => {
        const user = users?.find((us) => us.id === u);
        cellarmans.push({
          value: u,
          label: user?.username,
        });
      });
      setValue("cellarman_ids", cellarmans);

      setValue("expire_date", new Date(operation?.expire_date));

      setValue("note", operation?.note);

      if (lots?.find((l) => l.name === operation?.batch_name_out)) {
        setValue("batch_name_selector", {
          value: false,
          label: operation?.batch_name_out,
        });
        setValue("new_batch_name", "");
      } else {
        setValue("batch_name_selector", {
          value: true,
          label: getLabel("lotNewName"),
        });
      }
    }

    if (!id) {
      const savedProtocol = localStorage.getItem("selectedWinemakingProtocol");
      if (savedProtocol) {
        const { type: protocolType, id: protocolId } =
          JSON.parse(savedProtocol);
        if (protocolType && protocolId) {
          const winemakingProtocol = protocols?.find(
            (p) => p?.id === Number(protocolId)
          );
          if (
            winemakingProtocol &&
            ((type === "WINEMAKING" && protocolType === "PRE") ||
              (type === "WINEMAKINGPOST" && protocolType === "POST"))
          ) {
            setValue("protocol_id", {
              value: winemakingProtocol?.id,
              label: winemakingProtocol?.name,
            });
            localStorage.removeItem("selectedWinemakingProtocol");
          }
        }
      }
    }
  }, [operations, operation, lots, protocols]);

  useEffect(() => {
    if (!id) setValue("protocol_id");
  }, [protocol_subtype]);

  useEffect(() => {
    if (!protocol_id?.value) return;
    if (["CLEANING", "PHYSICAL", "STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(type) && id) {
      setOperationsP(
        [...operation?.treatments]
          ?.sort((a, b) => a?.day - b?.day)
          ?.map((s) => {
            return {
              ...s,
              product: s?.substance_id,
              product_name: s?.substance_name,
              opType: "WINEMAKINGPROTOCOL",
              operation: s?.operation || s?.type,
            };
          }) || []
      );
    } else {
      // setOperationsP(
      //   [...protocols
      //     ?.find((p) => p.id == protocol_id?.value)
      //     ?.operations || []]
      //     ?.sort((a, b) => Number(a?.day) - Number(b?.day))
      //     ?.map((s) => {
      //       return {
      //         ...s,
      //         value: s?.substance_id || s?.product,
      //         product:
      //           drugs?.filter(drug => drug?.id === (s?.substance_id || s?.product))
      //             ?.map((d) => ({
      //               ...d,
      //               value: d?.id,
      //               label: d?.name + (d?.unit ? " (" + d?.unit + ")" : ""),
      //             })) ?? [],
      //         product_name: s?.substance_name || s?.product_name,
      //         opType: "WINEMAKINGPROTOCOL",
      //       };
      //     }) || []
      // );
      const selectedProtocol = protocols?.find(
        (p) => p.id === protocol_id?.value
      );

      if (selectedProtocol) setProtocol(selectedProtocol);

      const ops =
        [...(selectedProtocol?.operations || [])]
          ?.sort((a, b) => Number(a?.day) - Number(b?.day))
          ?.map((op) => {
            const drug = drugs?.find(
              (d) => d?.id === (op?.substance_id || op?.product)
            );
            const product = drug && {
              ...drug,
              value: drug.id,
              label: `${drug.name}${drug.unit ? ` (${drug.unit})` : ""}`,
            };

            return {
              ...op,
              value: op?.substance_id || op?.product,
              product,
              // product_name: op?.substance_name || op?.product_name,
              opType: "WINEMAKINGPROTOCOL",
            };
          }) || [];

      setOperationsP(ops);
    }
  }, [protocol_id]);

  const getProtocolName = (p) => {
    const protocolSubtype =
      p?.protocol_subtype === "STABILIZATION_RESIN"
        ? getLabel("physicalProtocol_STAB") +
          " " +
          getLabel("physicalProtocolSTABILIZATION_RESIN")
        : p?.stabilization_type === "STABILIZATION_COLD"
        ? getLabel(`protocolOperation_${p?.protocol_subtype}`)?.toLowerCase() +
          " " +
          // getLabel(`physicalProtocol${p?.protocol_subtype}`) +
          // " - " +
          getLabel(`physicalProtocol${p?.stabilization_type}String`)
        : getLabel("physicalProtocol_FILTRATION");
          
    return p?.name + ` (${protocolSubtype?.toLowerCase?.()})`;   
  }

  const customIdInvalidStepsError = "custom-id-invalid-steps";
  const customIdInvalidResinPercentages = "custom-id-invalid-resin-perc";

  const submitData = async () => {
    const data = getValues();
    if (!data?.batch_name_selector?.value) {
      data.new_batch_name = data?.batch_name_selector?.label;
    }

    if (new Date(data?.expire_date?.setHours(23, 59, 59)) < new Date()) {
      setError("expire_date", {
        type: "manual",
        message: getLabel("errorDateInThePast"),
      });
      return;
    }

    const src_tank_ids =
      analysisType?.value === "tank" || ["CLEANING", "PHYSICAL", "STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(type)
        ? data?.src_tanks?.map((s) => s?.tankData?.id || s?.tankData?.value)
        : (data?.edit || [])
            ?.map((value, index) => (value === true ? index : -1))
            ?.filter((index) => index !== -1)
            ?.filter((item) => item !== null);

    const info = {
      analysisType: data?.analysisType?.value || "tank",
    };

    const src_tanks = src_tank_ids?.map((t) => {
      return { tank_id: t };
    });
    src_tanks[0] = { ...src_tanks[0], info: info };

    if (["STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(data?.protocol_id?.protocol_subtype)) {
      let index = 0;
      for (const src_tank of src_tanks) {
        src_tank.resin_quantity = Number(data?.physical_percentage?.[index] || 0);
        index += 1;
      }    
    }

    if (type !== "CLEANING") {
      for (let i = 0; i < src_tank_ids?.length; i++) {
        const t = tanks?.find((tt) => tt?.id == src_tank_ids[i]);
        if (Number(t?.quantity) <= 0 && data?.analysisType?.value === "tank") {
          setError(`src_tanks.${i}.tankData`, {
            type: "manual",
            message: getLabel("error0QuantityNewModify"),
          });
          return;
        }
        if (["PHYSICAL", "STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(type)) {
          // physical treatment
          if (data?.protocol_id?.protocol_subtype === "STABILIZATION_RESIN") {
            if (
              Array.isArray(data?.physical_percentage) &&
              data?.physical_percentage.length === src_tank_ids?.length
            ) {
              const physical_percentage =
                Number(data?.physical_percentage[i]) || 0;
              if (
                isNaN(physical_percentage) ||
                physical_percentage <= 0 ||
                physical_percentage > 100
              ) {
                setError(`physical_percentage.${i}`, {
                  type: "manual",
                  message: getLabel(
                    physical_percentage <= 0 ? "errorMinValue" : "errorMaxValue",
                    { value: physical_percentage <= 0 ? 1 : 100 }
                  ),
                });
                return;
              }
              toast.dismiss(customIdInvalidResinPercentages);
            } else {
              toast(getLabel("toast_treatmentMissingResinPercentages"), {
                toastId: customIdInvalidResinPercentages,
                type: toast.TYPE.ERROR,
                isLoading: false,
                position: toast.POSITION.BOTTOM_RIGHT,
              });
              return;
            }
          }
        }
      }
    }

    const requestPayload = {
      // tanks: dests,
      src_tanks: src_tanks,
      priority: data?.priority?.value,
      is_exact_date: isExactDate,
      start_date: new Date(
        data?.expire_date?.setHours(23, 59, 59)
      ).toISOString(),
      // expire_date: new Date(
      //   data?.expire_date?.setHours(23, 59, 59)
      // ).toISOString(),
      type: type,
      winery_id: activeWinery?.id,
      cellarman_ids: data?.cellarman_ids?.map((cellMan) => cellMan?.value),
      working_protocol_id: data?.protocol_id?.value,
      winemaking_protocol_id: data?.protocol_id?.value,
      ...(type === "CLEANING" && {
        expire_date: new Date(
          data?.expire_date?.setHours(23, 59, 59)
        ).toISOString()
      }),
      ...(type === "CLEANING" &&
        data?.protocol_id?.subtype === "CLEANING_MACHINES" && {
          machinery_id: data?.machinery_id?.value,
        }),
    };

    const tmp_treatments_data = [];

    const getSubstanceById = (id) => drugs?.find((s) => s?.id === Number(id));

    // Build from operationsP
    for (let i = 0; i < operationsP.length; i++) {
      const step = operationsP[i];
      const tmp = {
        id: Number(step.id),
        day: Number(step.day),
        phase: step.phase,
        operation: step.operation,
        goal: step.goal,
        note: step.note,
        protocol_id: data?.protocol_id?.value,
        info: {
          analysisType: data?.analysisType?.value || "tank",
        },
      };

      if (
        step.operation === "RINSE" ||
        step.operation === "CLEANING" ||
        step.operation === "VAPORIZATION"
      ) {
        tmp.protocol_type = "ADD";
        tmp.substance_id = step?.product?.id;
        tmp.quantity = Number(step?.quantity) || 0;
        tmp.work_time = Number(step?.work_time) || 0;
      } else if (step.operation === "ADD") {
        const substance = getSubstanceById(step?.product?.value);
        if (!substance) continue;

        const baseQuantity = Number(step.quantity) || 0;

        // Override quantity from data.column if present
        const override = Object.values(data.column || {}).find(
          (col) => Number(col.substance_id) === substance.id
        );
        const quantity =
          override && Number(override.new) >= 0
            ? Number(override.new)
            : baseQuantity;

        // const status = substanceStatusMap[substance.id] || { removed: false, added: false };
        // tmp.operation = "ADD";
        tmp.product = substance?.id;
        tmp.substance_name = substance?.name;
        tmp.quantity = Number(quantity);
        tmp.unit_id = substance.unit_id || substance?.unit_id;
        tmp.unit = substance.unit || substance?.unit;
        tmp.protocol_type =
          step.operation === "ADD" ? step?.functional : step.operation;
      } else if (step.operation === "HOMOGENIZATION_MASS") {
        tmp.quantity = Number(step?.quantity);
        tmp.motor_speed = Number(step?.motor_speed);
        tmp.work_time = Number(step?.work_time);
        tmp.pause_time = Number(step?.pause_time);
        tmp.iter_per_day = Number(step?.iter_per_day);
        tmp.homogenization_type = step?.homogenization_type?.value;
        tmp.work_time_unit = step?.work_time_unit || "min";
        tmp.protocol_type =
          step?.protocol_type?.replace("HOMOGENIZATION_", "") || null;
      }
      
      tmp_treatments_data.push(tmp);
    }

    // Validate step continuity (days: 1..N)
    if (!validateSteps(tmp_treatments_data)) {
      return;
    }

    requestPayload.treatments_data = tmp_treatments_data;
    console.log("requestPayload", requestPayload);
    // return;
    localStorage.setItem("wineMakingData", JSON.stringify(requestPayload));
    // navigate('/programming/DECANT/new')
    // return;
    const toastId = toast.loading(getLabel("toast_inProgress"), {
      type: toast.TYPE.INFO,
      position: toast.POSITION.BOTTOM_RIGHT,
      exclude: true,
    });
    let response = null;

    // if (id) {
    // response = await updateWorkingProtocol(id, requestPayload, axiosPrivate);
    // } else {
    if (type === "CLEANING") {
      if (id && !isDuplicate) {
        response = await updateOperation(id, requestPayload, axiosPrivate);
      } else {
        response = await newOperation(requestPayload, axiosPrivate);
      }
    } else {
      if (id && !isDuplicate) {
        requestPayload.operation_id = Number(id);
      }

      response = await applyWorkingProtocolOperation(
        requestPayload,
        axiosPrivate
      );
    }

    console.log(response);
    // return;
    toast.update(toastId, {
      render: response?.success
        ? getLabel(response?.success, { type: operation?.type || "" })
        : getLabel(response?.error),
      type:
        !response || response?.error ? toast.TYPE.ERROR : toast.TYPE.SUCCESS,
      isLoading: false,
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: 4000,
    });

    if (response && response?.success) {
      if (type === "CLEANING") {
        const updated_op = response?.updated_data || [];
        updateStore(updated_op);
      } else {
        const updated_ops = response?.updated_data || [];
        for (const updated_op of updated_ops) updateStore(updated_op);
      }

      navClickHandler();
    }
  };

  const updateStore = (payloadOp) => {
    if (id && !isDuplicate) {
      dispatch(updateOp(payloadOp));
    } else {
      dispatch(add(payloadOp));
    }
  };

  const navClickHandler = () => {
    if (id) {
      navigate(`/programming/${type}/${id}`);
    } else {
      navigate("/programming");
    }
  };

  const validateSteps = (tmp_treatments_data, is_toast = true) => {
    if (type === "CLEANING") {
      const days = tmp_treatments_data
        .map((t) => t.day)
        .filter(Number.isInteger);
      const sortedDays = [...new Set(days)].sort((a, b) => a - b);
      const max = sortedDays[sortedDays.length - 1] || 0;
      const expectedDays = Array.from({ length: max }, (_, i) => i + 1);

      const isSequential =
        sortedDays.length === expectedDays.length &&
        sortedDays.every((day, idx) => day === expectedDays[idx]);

      if (!isSequential) {
        if (is_toast)
          toast.error(getLabel("toast_invalidCleaningSteps"), {
            toastId: customIdInvalidStepsError,
            isLoading: false,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 4000,
          });
        return false;
      } else {
        if (is_toast) toast.dismiss(customIdInvalidStepsError);
        return true;
      }
    }
    return true;
  };

  const mapDestTanks = () => {
    return [...tanks];
    return tanks?.map((tank) => {
      return {
        name: tank?.name,
        batch_name: tank?.batch_name,
        quantity: Number(tank?.quantity),
        tankData: {
          ...tank,
          value: tank?.id,
          label: tank?.name,
          is_src: false,
          capacity: Number(tank?.capacity),
          quantity: Number(tank?.quantity),
          // is_planned_by_other_ops: isPlannedByOtherOps,
          // planned_quantity: isPlannedByOtherOps ?
          //   Number(tank?.planned_quantity) - Number(s?.arrow_quantity) : 0,
          // is_planned: isPlannedByOtherOps,
          // is_planned_state: tank?.is_planned_state,
          // planned_batch_name: isPlannedByOtherOps ? tank?.planned_batch_name : "",
          // planned_batch_type: tank?.planned_batch_type,
          // availability:
          //   Number(tank?.capacity) -
          //   Number(tank?.planned_quantity) +
          //   Number(s?.arrow_quantity),
          // lot:
          //   lots?.find((lot) => lot?.id === tank?.batch_id)?.name ||
          //   getLabel("tankState_EMPTY"),
          // programmed_as_src: tank?.programmed_as_src,
          // programmed_as_dest: tank?.programmed_as_dest,
          // is_planned_by_other_ops: tank?.is_planned && tank?.programmed_as_dest?.filter(p_id => id === null || p_id !== Number(id))?.length > 0,
        },
      };
    });
  };

  const filterProtocolsByType = () => {
    const isStabilization = ["STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(type);
    const isFiltration = type === "FILTRATION";
    const isPhysical = type === "PHYSICAL" || isStabilization || isFiltration;

    if (isPhysical)
      return (
        protocols
          ?.filter((p) => (p?.protocol_type === "PHYSICAL" && isPhysical && 
            (
              (protocol_subtype?.value === "physicalProtocol_STABILIZATION") 
              || (isFiltration && protocol_subtype?.value === "physicalProtocol_FILTRATION")
            )))
          ?.map((p) => {
            const name = getProtocolName(p);
            return { ...p, value: p?.id, label: name, name: name };
          }) || []
      );

    return protocols
      ?.filter((p) => p?.protocol_type === pType)
      ?.map?.((p) => {
        return {
          value: p?.id,
          label: p?.name,
          subtype: p?.protocol_subtype,
          name: `${p?.name} ${
            p?.protocol_subtype
              ? `(${getLabel(
                  `protocolSubtype_${p?.protocol_subtype}`
                )?.toLowerCase()})`
              : ""
          }`,
        };
      });
  };

  const renderStaticStabilization = () => {
    
    return (<div>
      <div className="protocol_info_container_row">
        <Label label={getLabel("stabilizationTemperature")}>
          {protocol_id?.operations?.[0]?.temperature || "-"} {"\u00b0C"}
        </Label>
        <Label label={getLabel("stabilizationWorkTime")}>
          {protocol_id?.operations?.[0]?.work_time}{" "}
          {getLabel(
            Number(protocol_id?.operations?.[0]?.work_time) > 1
              ? "repeatSize_HOUR"
              : "repeatSize_1HOUR"
          )?.toLowerCase?.()}
        </Label>
      </div>
    </div>)
  }

  const renderDynamicStabilization = () => {
    const treatmentData = operationsP?.[0] || {};

    return (<div>
      <div className="protocol_info_container_row">
        <Label label={getLabel("stabilizationTemperature")}>
          {treatmentData?.temperature || "-"} {"\u00b0C"}
        </Label>
        <Label
          label={getLabel("protocolStabilizationProduct")}
        >
          {treatmentData?.product_name || '-'}
        </Label>
        <Label
          label={getLabel("stabilizationQuantity")}
        >
          {treatmentData?.quantity || '-'} {treatmentData?.product_unit}
        </Label>
      </div>
      <div className="protocol_info_container_row">
        <Label
          label={getLabel("protocolWorkTime")}
        >
          {treatmentData?.work_time || '-'} {treatmentData?.work_time_unit || ''}
        </Label>
        <Label
          label={getLabel("protocolPauseTime")}
        >
          {treatmentData?.pause_time || '-'} {treatmentData?.pause_time_unit || ''}
        </Label>
        <Label
          label={getLabel("protocolMotorSpeed")}
        >
          {treatmentData?.motor_speed || '-'} %
        </Label>
        <Label
          label={getLabel("protocolHomogenizationIterPerDay")}
        >
          x{treatmentData?.iter_per_day}
        </Label>
      </div>
    </div>)
  }

  if (operation && operation?.completion_date !== null)
    return <h5 className="alert_no_info">{getLabel("noPendingOperation")}</h5>;

  return (
    <div className="operation_form_wrapper">
      <form onSubmit={handleSubmit(submitData)} autoComplete="off" noValidate>
        <h4>
          {getLabel(
            ["PHYSICAL", "STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(type)
              ? "wineMakingPhysicalProtocols"
              : type === "CLEANING"
              ? "wineMakingCIPProtocols"
              : type === "WINEMAKINGPOST"
              ? "wineMakingPostProtocols"
              : "wineMakingProtocols"
          )}
        </h4>
        <div className="operation_form_wrapper_row">
          {!id && type === "PHYSICAL" && <Select
            label={getLabel("protocolSubtype")}
            placeholder={getLabel("protocolSubtypePlaceholder")}
            errors={errors[`protocol_subtype`]}
            // required={getLabel("protocolNameRequiredError")}
            name={`protocol_subtype`}
            control={control}
            options={[
              { value: "physicalProtocol_STABILIZATION",
                label: getLabel("physicalProtocol_STABILIZATION"),
              },
              { value: "physicalProtocol_FILTRATION",
                label: getLabel("physicalProtocol_FILTRATION"),
              },
            ]}
          />}
          <Select
            label={getLabel("protocolName")}
            placeholder={getLabel("protocolNamePlaceholder")}
            errors={errors[`protocol_id`]}
            required={getLabel("protocolNameRequiredError")}
            name={`protocol_id`}
            control={control}
            isDisabled={!protocol_subtype && ["PHYSICAL", "STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(type)}
            options={filterProtocolsByType()}
          />
          {type === "CLEANING" && protocol?.protocol_subtype && (
            <Label label={getLabel("protocolSubtype_CLEANING")}>
              {getLabel(`protocolSubtype_${protocol?.protocol_subtype}`)}
            </Label>
          )}
        </div>
        {protocol_id?.value && !["PHYSICAL", "STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(type) && (
          <div className="operation_form_wrapper_row vertical">
            <TableEdit
              defaultData={operationsP}
              columns={columns}
              // errors={errors}
              errors={modalErrors}
              control={modalControl}
              isEditable={type === "CLEANING"}
              isLegend={false}
              isSearchBar={false}
              // setValue={setValue}
              setValue={modalSetValue}
              getValues={modalGetValues}
              type={"winemaking"}
              modalHandler={handleOpenModal}
              setModifyMode={setModifyMode}
              setRows={setOperationsP}
            />
          </div>
        )}
        {protocol_id?.value && (["STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(operation?.type) || type === "PHYSICAL") && (
          <div className="operation_form_wrapper_row">
            {protocol_id?.protocol_subtype === "STABILIZATION_RESIN" && (
              <Label label={getLabel("stabilizationPh")}>
                {protocol_id?.operations?.[0]?.stabilization_ph}
              </Label>
            //   <Input
            //     name="stabilization_ph"
            //     register={register}
            //     type="number"
            //     step="0.1"
            //     label={getLabel("stabilizationPh")}
            //     placeholder={getLabel("stabilizationPhPlaceholder")}
            //     error={errors["stabilization_ph"]}
            //     required={getLabel("inputRequiredError")}
            //     regExpPattern={{
            //       value: NEGATIVE_POSITIVE_INT_FLOAT_REGEX,
            //       message: getLabel("errorNegativePositiveIntFloat"),
            //     }}
            //     minLength={{
            //       value: 1,
            //       message: getLabel("errorMinLength", { value: 1 }),
            //     }}
            //     maxLength={{
            //       value: 5,
            //       message: getLabel("errorMaxLength", { value: 5 }),
            //     }}
            //     min={{
            //       value: -2,
            //       message: getLabel("errorMinValue", { value: -2 }),
            //     }}
            //     max={{
            //       value: 2,
            //       message: getLabel("errorMaxValue", { value: 2 }),
            //     }}
            //     tooltip={getLabel("stabilizationPhTooltip")}
            // />
            )}
            {protocol_id?.protocol_subtype === "STABILIZATION_STATIC" && renderStaticStabilization()}
            {protocol_id?.protocol_subtype === "STABILIZATION_DYNAMIC" && renderDynamicStabilization()}
          </div>
        )}

        {!id &&
          type === "CLEANING" &&
          protocol?.protocol_subtype === "CLEANING_MACHINES" &&
          (() => {
            const bottlingMachines =
              machineries?.length > 0
                ? machineries?.map((m) => {
                    return { value: m?.id, label: m?.name };
                  })
                : [];

            return bottlingMachines?.length === 0 ? (
              // && src_tanks?.filter((t) => t?.value !== null)?.length === 0
              <>
                <h4>{getLabel("operationBottlingMachinesConfiguration")}</h4>
                <h5 className="alert_no_info">
                  {getLabel("operationWineMakingCIPNoDestBottlingMachines")}
                </h5>
              </>
            ) : (
              <>
                <h4>{getLabel("operationBottlingMachinesConfiguration")}</h4>
                <Select
                  label={getLabel("protocolCleaningBottlingMachines")}
                  placeholder={getLabel(
                    "protocolCleaningBottlingMachinesPlaceholder"
                  )}
                  errors={errors}
                  required={getLabel("inputRequiredError")}
                  name={`machinery_id`}
                  control={control}
                  options={bottlingMachines}
                />
              </>
            );
          })()}
        {
        //!id &&
          (type !== "CLEANING" ||
            protocol?.protocol_subtype === "CLEANING_TANKS") &&
          (() => {
            // Filtra i tank disponibili
            const validTanks = tanks?.filter((tank) => {
              const matchesState =
                (type === "WINEMAKING" && tank.state === "MUST") ||
                (type === "WINEMAKINGPOST" && tank.state === "WINE") ||
                type === "CLEANING";

              const isTankEligible =
                type === "CLEANING"
                  ? Number(tank?.quantity) === 0 || !tank?.batch_id
                  : Number(tank?.quantity) > 0;

              return matchesState && isTankEligible;
            });

            // Escludi quelli già selezionati
            const unselectedTanks = validTanks?.filter(
              (tank) =>
                !src_tanks?.some?.((field) => field.tankData.value == tank.id)
            );

            // Se non ci sono tank validi non ancora selezionati,
            // ma almeno uno è già stato selezionato, NON mostrare alert
            return !id && unselectedTanks?.length === 0 &&
              src_tanks?.filter((t) => t?.value !== null)?.length === 0 ? (
              <>
                <h4>{getLabel("operationTanksConfiguration")}</h4>
                <h5 className="alert_no_info">
                  {getLabel(
                    type === "WINEMAKING"
                      ? "operationWineMakingPreNoDestTanks"
                      : type === "WINEMAKINGPOST"
                      ? "operationWineMakingPostNoDestTanks"
                      : "operationWineMakingCIPNoDestTanks"
                  )}
                </h5>
              </>
            ) : (
              <LotOrTankOperationSelector
                type={type}
                analysisType={analysisType}
                control={control}
                errors={errors}
                register={register}
                protocol_id={protocol_id}
                lots={lots}
                tanks={mapDestTanks()}
                src_lots={src_lots}
                src_tanks={src_tanks}
                srcBatchFieldArray={srcBatchFieldArray}
                srcTankFieldArray={srcTankFieldArray}
                srcTankEnabledFieldArray={srcTankEnabledFieldArray}
                setSrcTankEnabledFieldArray={setSrcTankEnabledFieldArray}
                getValues={getValues}
                setValue={setValue}
                getLabel={getLabel}
                onlyTanks={true}
              />
            );
          })()}
        <div className="operation_form_wrapper_row">
          <MultipleSelect
            name={`cellarman_ids`}
            control={control}
            label={getLabel("operationUser")}
            placeholder={getLabel("operationUserPlaceholder")}
            error={errors["cellarman_ids"]}
            required={getLabel("inputRequiredError")}
            options={users?.map?.((user) => {
              return {
                value: user?.id,
                label: user?.username,
              };
            })}
          />
          <Select
            name={"priority"}
            control={control}
            label={getLabel("operationPriority")}
            placeholder={getLabel("operationPriorityPlaceholder")}
            error={errors}
            required={getLabel("inputRequiredError")}
            options={[
              {
                label: getLabel("lotQuality_HIGH"),
                value: 2,
              },
              {
                label: getLabel("lotQuality_MEDIUM"),
                value: 1,
              },
              {
                label: getLabel("lotQuality_LOW"),
                value: 0,
              },
            ]}
            // defaultValue={{
            //     value: "",
            //     label: "",
            //   }}
          />
          <DatePicker
            name="expire_date"
            control={control}
            label={getLabel(
              (["CLEANING", "PHYSICAL", "STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(type))
                ? "operationExpireDateForm"
                : "operationProtocolStartDateForm"
            )}
            error={errors["expire_date"]}
            required={getLabel("inputRequiredError")}
            dateFormat={configs.shortDateFormat}
            placeholder={getLabel("expiringDatePlaceholder")}
          />
          {["CLEANING", "PHYSICAL", "STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(type) && getValues("expire_date") && (
            <Input
              name={"expire_date_exact"}
              register={register}
              label={getLabel("isExactDate")}
              error={errors["expire_date_exact"]}
              // isLabel={false}
              type={"checkbox"}
              checked={isExactDate}
              onChange={() => setIsExactDate((prev) => !prev)}
              customClassName={"confirm no_margin_top"}
              tooltip={getLabel("destemmingExactDate")}
            />
          )}
        </div>
        {/* <div className="operation_form_wrapper_row">
          <TextArea
            name="note"
            register={register}
            label={getLabel("clientNotes")}
            placeholder={getLabel("clientNotesPlaceholder")}
            error={errors["note"]}
          />
        </div> */}
        <div className="operation_form_wrapper_row">
          <Submit label={getLabel("submitForm")}></Submit>
        </div>
      </form>
      <WineMakingProtocolRowModal
        isOpen={isModalOpen}
        // onConfirm={updateTableData}
        onConfirm={modalHandleSubmit(updateTableData)}
        handleSubmit={modalHandleSubmit}
        onClose={handleCloseModal}
        description={getLabel("modalLogout")}
      >
        <WineMakingProtocolModalChildren
          type={type === "CLEANING" ? "CIP" : type}
          operation={!id ? modalOperation : watchedOperation}
          register={modalRegister}
          errors={modalErrors}
          control={modalControl}
          drugs={drugs}
          getLabel={getLabel}
          getValues={modalGetValues}
          setValue={modalSetValue}
          setCheckboxState={setCheckboxState}
        />
        {/* <TransferForm
          lots={lots}
          tanks={tanks}
          type={type}
          activeWinery={activeWinery}
          configs={configs}
          isWineMakingApplication={true}
        /> */}
      </WineMakingProtocolRowModal>
    </div>
  );
};

export default WineMakingForm;
